import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Truck, Wallet, Receipt, AlertTriangle, ShoppingCart, ClipboardList, PackageCheck, Boxes } from "lucide-react";

function StatCard({ label, value, sublabel, icon: Icon, accent = "bg-orange-500/10", to, testid }) {
  const content = (
    <div className="surface-card p-5 hover:-translate-y-1 hover:shadow-sm transition-all cursor-pointer h-full" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
        <div className={`w-9 h-9 rounded-xl ${accent} grid place-items-center`}>{Icon ? <Icon className="w-4 h-4" /> : null}</div>
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-3">{value}</div>
      {sublabel ? <div className="text-[11px] text-muted-foreground mt-1">{sublabel}</div> : null}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

export default function OrgOverview() {
  const { t, i18n } = useTranslation();
  const { user, tenant } = useAuth();
  const [k, setK] = useState(null);
  const [pr, setPr] = useState({ open: 0, count: 0 });

  useEffect(() => {
    api.get("/org/dashboard-kpis").then(({ data }) => setK(data)).catch(() => setK(null));
    Promise.all([
      api.get("/procurement/purchase-orders").catch(() => ({ data: [] })),
      api.get("/procurement/requisitions").catch(() => ({ data: [] })),
      api.get("/procurement/grn").catch(() => ({ data: [] })),
    ]).then(([po, prRes, grn]) => {
      const openPO = po.data.filter((x) => !["FULFILLED", "REJECTED", "CANCELLED"].includes((x.status || "").toUpperCase()));
      const openPR = prRes.data.filter((x) => !["APPROVED", "REJECTED", "FULFILLED", "CANCELLED"].includes((x.status || "").toUpperCase()));
      setPr({
        po_count: po.data.length,
        po_open: openPO.length,
        po_open_value: openPO.reduce((s, x) => s + Number(x.total || 0), 0),
        pr_count: prRes.data.length,
        pr_open: openPR.length,
        grn_count: grn.data.length,
      });
    });
  }, []);

  // Localised month name (Gujarati / Hindi calendars fall back to native month name).
  const localeMap = { en: "en", gu: "gu-IN", hi: "hi-IN" };
  const locale = localeMap[i18n.language] || "default";
  const monthName = new Date().toLocaleString(locale, { month: "long" }).toUpperCase();

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" data-testid="org-overview">
      <div>
        <div className="label-overline">{tenant?.name || t("overview.orgFallback")}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">{t("overview.welcome", { name: user?.full_name?.split(" ")[0] || "" })}</h1>
        <p className="text-muted-foreground mt-2">{t("overview.subtitle")}</p>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold mb-3">{monthName} · {t("overview.monthSuffix")}</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label={t("overview.kpi.dispatchSqm")} value={`${(k?.dispatch_month?.sqm || 0).toLocaleString()} sqm`} sublabel={t("overview.kpi.dispatchSqmSub", { count: k?.dispatch_month?.count || 0 })} icon={Truck} accent="bg-blue-500/10" to="/org/dispatch" testid="kpi-dispatch-sqm" />
          <StatCard label={t("overview.kpi.dispatchValue")} value={`₹${Number(k?.dispatch_month?.value || 0).toLocaleString()}`} sublabel={t("overview.kpi.dispatchValueSub")} icon={Truck} accent="bg-blue-500/10" to="/org/dispatch" testid="kpi-dispatch-value" />
          <StatCard label={t("overview.kpi.paymentsReceived")} value={`₹${Number(k?.payments_month?.total || 0).toLocaleString()}`} sublabel={t("overview.kpi.paymentsSub", { count: k?.payments_month?.count || 0 })} icon={Wallet} accent="bg-emerald-500/10" to="/org/finance/receivables" testid="kpi-payments-received" />
          <StatCard label={t("overview.kpi.arOutstanding")} value={`₹${Number(k?.ar_outstanding || 0).toLocaleString()}`} sublabel={t("overview.kpi.arSub")} icon={Receipt} accent="bg-amber-500/10" to="/org/finance/receivables" testid="kpi-ar-outstanding" />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold mb-3">{t("overview.purchasePulse")}</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label={t("overview.kpi.openPos")} value={pr.po_open || 0} sublabel={t("overview.kpi.openPosSub", { count: pr.po_count || 0 })} icon={ShoppingCart} accent="bg-violet-500/10" to="/org/purchase-orders" testid="kpi-po-open" />
          <StatCard label={t("overview.kpi.openPoValue")} value={`₹${Number(pr.po_open_value || 0).toLocaleString()}`} sublabel={t("overview.kpi.openPoValueSub")} icon={ShoppingCart} accent="bg-violet-500/10" to="/org/purchase-orders" testid="kpi-po-value" />
          <StatCard label={t("overview.kpi.pendingPrs")} value={pr.pr_open || 0} sublabel={t("overview.kpi.pendingPrsSub", { count: pr.pr_count || 0 })} icon={ClipboardList} accent="bg-fuchsia-500/10" to="/org/requisitions" testid="kpi-pr-open" />
          <StatCard label={t("overview.kpi.grnsFiled")} value={pr.grn_count || 0} sublabel={t("overview.kpi.grnsSub")} icon={PackageCheck} accent="bg-rose-500/10" to="/org/grn" testid="kpi-grn-count" />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold mb-3">{t("overview.liabilities")}</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <StatCard label={t("overview.kpi.apOutstanding")} value={`₹${Number(k?.ap_outstanding || 0).toLocaleString()}`} sublabel={t("overview.kpi.apSub")} icon={AlertTriangle} accent="bg-rose-500/10" to="/org/finance/payables" testid="kpi-ap-outstanding" />
          <StatCard label={t("overview.kpi.netWc")} value={`₹${(Number(k?.ar_outstanding || 0) - Number(k?.ap_outstanding || 0)).toLocaleString()}`} sublabel={t("overview.kpi.netWcSub")} icon={Boxes} accent="bg-orange-500/10" testid="kpi-net-wc" />
        </div>
      </div>
    </div>
  );
}
